import { useEffect, useRef, useState } from "react";
import { Characteristic, Schema } from "../types";
import Title from "./ui/Title";

interface CharacteristicsProps {
    editableCharacteristics: Record<string, Characteristic>;
    setEditableCharacteristics: React.Dispatch<React.SetStateAction<Record<string, Characteristic>>>;
    typedSchema: Schema;
}

const Characteristics = ({editableCharacteristics, setEditableCharacteristics, typedSchema}: CharacteristicsProps) => {
    const characteristicsContainerRef = useRef<HTMLDivElement>(null);

    const [characteristicsAtBottom, setCharacteristicsAtBottom] = useState(false);

    useEffect(() => {
        const handleCharacteristicsScroll = () => {
            const container = characteristicsContainerRef.current;
            if (container) {
                const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 5;
                console.log('isAtBottom', isAtBottom);
                setCharacteristicsAtBottom(isAtBottom);
            }
        };
  
        // Add event listeners
        const characteristicsContainer = characteristicsContainerRef.current;
        
        if (characteristicsContainer) {
            characteristicsContainer.addEventListener('scroll', handleCharacteristicsScroll);
            // Initial check
            handleCharacteristicsScroll();
        }
        
        // Cleanup
        return () => {
            if (characteristicsContainer) {
                characteristicsContainer.removeEventListener('scroll', handleCharacteristicsScroll);
            }
        };
    }, []);

    useEffect(() => {
        const characteristicsContainer = characteristicsContainerRef.current;
        if (characteristicsContainer) {
            const isAtBottom = characteristicsContainer.scrollHeight - characteristicsContainer.scrollTop <= characteristicsContainer.clientHeight + 5;
            setCharacteristicsAtBottom(isAtBottom);
        }
    }, [editableCharacteristics]);


    // Sort characteristics to place those with values at the top
    const getSortedCharacteristics = () => {
        return Object.entries(editableCharacteristics).sort(([nameA, valueA], [nameB, valueB]) => {
            // If A has an answer and B doesn't, A comes first
            if (valueA.answer && !valueB.answer) return -1;
            // If B has an answer and A doesn't, B comes first
            if (!valueA.answer && valueB.answer) return 1;
            // Otherwise, keep original order
            if (nameA.includes('Meldingsclassificatie')) return -1;
            if (nameB.includes('Meldingsclassificatie')) return 1;

            return 0;
        });
    };

     // Helper function to handle changes to the editable characteristics
     const handleCharacteristicChange = (key: string, field: 'answer' | 'explanation', value: string) => {
        setEditableCharacteristics(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                [field]: value,
                confidence: null, // Remove confidence when edited by user
                isEdited: true
            }
        }));
    };

    // Render the appropriate input field based on characteristic type
    const renderInputField = (key: string, value: {
        answer: string;
        explanation: string;
        confidence: number | null;
        isEdited: boolean;
    }) => {
        const characteristicSchema = typedSchema[key];
        
        if (!characteristicSchema) {
            // Fallback to text input if schema not found
            return (
                <input
                    type="text"
                    value={value.answer}
                    onChange={(e) => handleCharacteristicChange(key, 'answer', e.target.value)}
                    className="w-full p-1 border border-gray-300 rounded"
                />
            );
        }

        switch (characteristicSchema.type) {
            case 'Getal':
                return (
                    <input
                        type="number"
                        value={value.answer}
                        onChange={(e) => handleCharacteristicChange(key, 'answer', e.target.value)}
                        className="w-full p-1 border border-gray-300 rounded"
                    />
                );
            case 'Meervoudige opsom':
                return (
                    <select
                        value={value.answer}
                        onChange={(e) => handleCharacteristicChange(key, 'answer', e.target.value)}
                        className="w-full p-1 border border-gray-300 rounded"
                    >
                        <option value="">Select an option</option>
                        {characteristicSchema.answer_options?.map((option: string) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                );
            case 'Ja/Nee':
                return (
                    <select
                        value={value.answer}
                        onChange={(e) => handleCharacteristicChange(key, 'answer', e.target.value)}
                        className="w-full p-1 border border-gray-300 rounded"
                    >
                        <option value="">Select an option</option>
                        <option value="Ja">Ja</option>
                        <option value="Nee">Nee</option>
                    </select>
                );
            case 'date':
                return (
                    <input
                        type="date"
                        value={value.answer}
                        onChange={(e) => handleCharacteristicChange(key, 'answer', e.target.value)}
                        className="w-full p-1 border border-gray-300 rounded"
                    />
                );
            default:
                return (
                    <input
                        type="text"
                        value={value.answer}
                        onChange={(e) => handleCharacteristicChange(key, 'answer', e.target.value)}
                        className="w-full p-1 border border-gray-300 rounded"
                    />
                );
        }
    };

   
    // Render confidence display
    const renderConfidence = (value: {
        answer: string;
        explanation: string;
        confidence: number | null;
        isEdited: boolean;
    }) => {
        if (value.isEdited || value.confidence === null) {
            return <span className="text-xs italic text-gray-500">(User edited)</span>;
        }
        
        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                value.confidence >= 0.7 ? 'bg-green-100 text-green-800' :
                value.confidence >= 0.4 ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
            }`}>
                {Math.round(value.confidence * 100)}%
            </span>
        );
    };

    return (
        <div className="flex flex-col bg-white rounded-lg shadow-md py-4 h-[60vh] lg:h-[70vh]">
            <div className="px-4"><Title title="Characteristics" /></div>
            <div className="h-fit relative">
                <div 
                    ref={characteristicsContainerRef}
                    className="h-[60vh] overflow-y-auto relative"
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                        {getSortedCharacteristics().map(([key, value]) => (
                            <div key={key} className="bg-white border rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow">
                                <div className="mb-2">
                                    <h4 className="text-sm font-medium text-gray-900 break-words w-full">{key}</h4>
                                    {typedSchema[key]?.description_value && (
                                        <p className="text-xs text-gray-500 mt-1">{typedSchema[key].description_value}</p>
                                    )}
                                </div>
                                
                                <div className="mb-2">
                                    <label className="block text-xs font-medium text-gray-500 mb-1">Answer</label>
                                    {renderInputField(key, value)}
                                </div>
                                
                                <div className="mb-2">
                                    <label className="block text-xs font-medium text-gray-500 mb-1">Explanation</label>
                                    <textarea
                                        value={value.explanation}
                                        onChange={(e) => handleCharacteristicChange(key, 'explanation', e.target.value)}
                                        className="w-full p-2 border border-gray-300 rounded text-sm"
                                        rows={6}
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1">Confidence</label>
                                    <div>{renderConfidence(value)}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {!characteristicsAtBottom && (
                    <div className="absolute bottom-0 left-0 right-0 h-48 w-full pointer-events-none bg-gradient-to-t from-white to-transparent" />
                )}
            </div>
        </div>
    );
};

export default Characteristics;
